/** Verificación en dos pasos: activar con QR, ver códigos de recuperación, regenerarlos y desactivar. */
import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Checkbox, Code, CopyButton, Group, Image, Modal, Paper, PasswordInput, SimpleGrid, Stack, Text, TextInput } from '@mantine/core';
import { IconCheck, IconCopy, IconDownload, IconShieldCheck, IconShieldOff } from '@tabler/icons-react';
import QRCode from 'qrcode';
import { api } from '../api';
import { avisarError, avisarOk } from '../comun';
import { useSesion } from '../sesion';

/** Código QR como imagen (se genera en el navegador: el secreto no sale de la página). */
export function CodigoQr({ contenido, tamano = 200 }: { contenido: string; tamano?: number }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let vigente = true;
    void QRCode.toDataURL(contenido, { width: tamano * 2, margin: 1, errorCorrectionLevel: 'M' }).then((u) => vigente && setUrl(u));
    return () => {
      vigente = false;
    };
  }, [contenido, tamano]);
  return url ? <Image src={url} alt="Código QR" w={tamano} h={tamano} style={{ imageRendering: 'pixelated', background: '#fff', padding: 6, borderRadius: 8 }} /> : null;
}

function descargarTexto(nombre: string, texto: string) {
  const url = URL.createObjectURL(new Blob([texto], { type: 'text/plain;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function CodigosRecuperacion({ codigos, onListo }: { codigos: string[]; onListo: () => void }) {
  const [guardados, setGuardados] = useState(false);
  const texto = `Códigos de recuperación de GestionPYME\n(cada uno sirve una sola vez)\n\n${codigos.join('\n')}\n`;
  return (
    <Stack>
      <Alert color="yellow" title="Guarda estos códigos ahora">
        Si pierdes el móvil, son la única forma de entrar sin él. Se muestran <b>una sola vez</b>. Cada uno vale una vez. Guárdalos en un gestor de contraseñas o impresos, no en el mismo móvil.
      </Alert>
      <Paper withBorder p="md">
        <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="xs">
          {codigos.map((c) => (
            <Code key={c} fz="lg" ta="center">
              {c}
            </Code>
          ))}
        </SimpleGrid>
      </Paper>
      <Group>
        <CopyButton value={texto}>
          {({ copied, copy }) => (
            <Button variant="light" leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />} onClick={copy}>
              {copied ? 'Copiados' : 'Copiar'}
            </Button>
          )}
        </CopyButton>
        <Button variant="light" leftSection={<IconDownload size={16} />} onClick={() => descargarTexto('codigos-recuperacion-gestionpyme.txt', texto)}>
          Descargar
        </Button>
      </Group>
      <Checkbox label="Ya los he guardado en un lugar seguro" checked={guardados} onChange={(e) => setGuardados(e.currentTarget.checked)} />
      <Button disabled={!guardados} onClick={onListo}>
        Continuar
      </Button>
    </Stack>
  );
}

/** Asistente de activación: QR → código de la app → códigos de recuperación. */
export function ActivarDobleFactor({ onTerminado }: { onTerminado: () => void }) {
  const [paso, setPaso] = useState<'inicio' | 'qr' | 'codigos'>('inicio');
  const [datos, setDatos] = useState<{ secreto: string; uri: string } | null>(null);
  const [codigo, setCodigo] = useState('');
  const [codigos, setCodigos] = useState<string[]>([]);
  const [trabajando, setTrabajando] = useState(false);

  const preparar = async () => {
    setTrabajando(true);
    try {
      setDatos(await api.post('/auth/2fa/preparar'));
      setPaso('qr');
    } catch (e) {
      avisarError(e);
    } finally {
      setTrabajando(false);
    }
  };
  const activar = async () => {
    setTrabajando(true);
    try {
      const r = await api.post<{ codigosRecuperacion: string[] }>('/auth/2fa/activar', { codigo });
      setCodigos(r.codigosRecuperacion);
      setPaso('codigos');
    } catch (e) {
      avisarError(e);
    } finally {
      setTrabajando(false);
    }
  };

  if (paso === 'codigos') return <CodigosRecuperacion codigos={codigos} onListo={onTerminado} />;
  if (paso === 'qr' && datos) {
    return (
      <Stack>
        <Text size="sm">
          <b>1.</b> Abre tu app de verificación (Google Authenticator, <b>Contraseñas de Apple</b>, Authy, 1Password…) y añade una cuenta escaneando este código.
        </Text>
        <Group align="flex-start" wrap="wrap">
          <CodigoQr contenido={datos.uri} />
          <Stack gap={4} style={{ flex: 1, minWidth: 220 }}>
            <Text size="sm" c="dimmed">
              ¿No puedes escanearlo? Introduce esta clave a mano (tipo «basado en tiempo»):
            </Text>
            <Code fz="md" style={{ wordBreak: 'break-all' }} aria-label="Clave manual">
              {datos.secreto.replace(/(.{4})/g, '$1 ').trim()}
            </Code>
          </Stack>
        </Group>
        <Text size="sm">
          <b>2.</b> Escribe el código de 6 cifras que muestra la app para confirmar:
        </Text>
        <TextInput
          label="Código de la app"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={7}
          value={codigo}
          onChange={(e) => setCodigo(e.currentTarget.value)}
          onKeyDown={(e) => e.key === 'Enter' && codigo.replace(/\s/g, '').length === 6 && void activar()}
          w={200}
        />
        <Group>
          <Button onClick={activar} loading={trabajando} disabled={codigo.replace(/\s/g, '').length !== 6}>
            Activar
          </Button>
          <Button variant="subtle" onClick={() => setPaso('inicio')}>
            Cancelar
          </Button>
        </Group>
      </Stack>
    );
  }
  return (
    <Stack>
      <Text size="sm">
        Además de la contraseña, se pedirá un código de 6 cifras de tu móvil cada vez que entres. Funciona sin conexión y con la app que ya uses (Google Authenticator o <b>Contraseñas de Apple</b>).
      </Text>
      <Group>
        <Button leftSection={<IconShieldCheck size={16} />} onClick={preparar} loading={trabajando}>
          Activar verificación en dos pasos
        </Button>
      </Group>
    </Stack>
  );
}

/** Sección de la pantalla «Mi cuenta»: estado y acciones. */
export function SeguridadCuenta() {
  const s = useSesion();
  const [accion, setAccion] = useState<null | 'desactivar' | 'codigos'>(null);
  const [clave, setClave] = useState('');
  const [codigo, setCodigo] = useState('');
  const [nuevos, setNuevos] = useState<string[] | null>(null);
  const activo = !!s.usuario?.totpActivo;

  const cerrar = () => {
    setAccion(null);
    setClave('');
    setCodigo('');
  };
  const ejecutar = async () => {
    try {
      if (accion === 'desactivar') {
        await api.post('/auth/2fa/desactivar', { clave, codigo });
        avisarOk('Verificación en dos pasos desactivada');
        cerrar();
        await s.recargar();
      } else {
        const r = await api.post<{ codigosRecuperacion: string[] }>('/auth/2fa/codigos', { clave, codigo });
        cerrar();
        setNuevos(r.codigosRecuperacion);
        await s.recargar();
      }
    } catch (e) {
      avisarError(e);
    }
  };

  return (
    <Paper withBorder p="md" maw={560}>
      <Group justify="space-between" mb="xs">
        <Text fw={600}>Verificación en dos pasos</Text>
        {activo ? (
          <Badge color="teal" leftSection={<IconShieldCheck size={12} />}>
            Activada
          </Badge>
        ) : (
          <Badge color="orange" leftSection={<IconShieldOff size={12} />}>
            Desactivada
          </Badge>
        )}
      </Group>
      {activo ? (
        <Stack gap="xs">
          <Text size="sm" c="dimmed">
            Te quedan <b>{s.seguridad?.codigosRecuperacionRestantes ?? 0}</b> códigos de recuperación sin usar.
          </Text>
          <Group>
            <Button variant="light" onClick={() => setAccion('codigos')}>
              Generar códigos nuevos
            </Button>
            {!s.seguridad?.exigir2fa && (
              <Button variant="subtle" color="red" onClick={() => setAccion('desactivar')}>
                Desactivar
              </Button>
            )}
          </Group>
        </Stack>
      ) : s.seguridad && !s.seguridad.cifradoDisponible ? (
        <Alert color="red">El servidor no tiene configurada la clave de cifrado (CLAVE_CIFRADO), así que no se puede activar. Mira app/README.md.</Alert>
      ) : (
        <ActivarDobleFactor onTerminado={() => void s.recargar()} />
      )}
      <Modal opened={accion !== null} onClose={cerrar} title={accion === 'desactivar' ? 'Desactivar la verificación en dos pasos' : 'Generar códigos de recuperación nuevos'}>
        <Stack>
          <Text size="sm" c="dimmed">
            Por seguridad, confirma tu contraseña y un código vigente de la app (o uno de recuperación).
            {accion === 'codigos' && ' Los códigos anteriores dejarán de valer.'}
          </Text>
          <PasswordInput label="Contraseña" value={clave} onChange={(e) => setClave(e.currentTarget.value)} autoComplete="current-password" />
          <TextInput label="Código" inputMode="numeric" value={codigo} onChange={(e) => setCodigo(e.currentTarget.value)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={cerrar}>
              Cancelar
            </Button>
            <Button color={accion === 'desactivar' ? 'red' : undefined} disabled={!clave || codigo.trim().length < 6} onClick={ejecutar}>
              {accion === 'desactivar' ? 'Desactivar' : 'Generar'}
            </Button>
          </Group>
        </Stack>
      </Modal>
      <Modal opened={nuevos !== null} onClose={() => setNuevos(null)} title="Códigos de recuperación nuevos" size="lg" closeOnClickOutside={false}>
        {nuevos && <CodigosRecuperacion codigos={nuevos} onListo={() => setNuevos(null)} />}
      </Modal>
    </Paper>
  );
}
